// src/pages/ProfilePage.jsx — Profile, Password Management & Active Sessions
import React, { useMemo, useState, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import toast from "react-hot-toast";
import { Mail, Shield, Phone, MapPin, Save, Camera, Key } from "lucide-react";
import PasswordInput from "../components/common/PasswordInput";
import PasswordStrengthMeter from "../components/common/PasswordStrengthMeter";
import ActiveSessions from "../components/security/ActiveSessions";

function initialsOf(x) {
  if (!x) return "U";
  const parts = String(x).trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function ProfilePage() {
  const { user } = useAuth();
  const fileInputRef = useRef(null);

  const base = useMemo(() => ({
    name: user?.name || "",
    email: user?.email || "",
    role: user?.role || "User",
    phone: user?.phone || "",
    address: user?.address || "",
    avatar: user?.avatar || "",
  }), [user]);

  const [form, setForm] = useState(base);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  // Password change states
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [signOutOthers, setSignOutOthers] = useState(true);
  const [changingPassword, setChangingPassword] = useState(false);

  const onChange = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const onSave = async () => {
    setSaving(true);
    try {
      await api.put("/employees/profile", { name: form.name, phone: form.phone, address: form.address });
      toast.success("Profile saved!");
    } catch {
      toast.error("Failed to save profile.");
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error("File is too large. Max 2MB.");
      return;
    }

    setUploadingAvatar(true);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const base64Data = ev.target.result;
      try {
        await api.put("/employees/me/avatar", { avatar: base64Data });
        setForm(p => ({ ...p, avatar: base64Data }));
        
        // Update user context manually
        const stored = JSON.parse(localStorage.getItem('crm_user'));
        if (stored) {
          stored.avatar = base64Data;
          localStorage.setItem('crm_user', JSON.stringify(stored));
        }
        window.location.reload();
        toast.success("Profile picture updated!");
      } catch (err) {
        toast.error("Failed to upload profile picture.");
      } finally {
        setUploadingAvatar(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!currentPassword) {
      toast.error("Please enter your current password");
      return;
    }
    if (!newPassword || newPassword.length < 8) {
      toast.error("New password must be at least 8 characters long");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("New password and confirm password do not match");
      return;
    }

    setChangingPassword(true);
    try {
      await api.post("/auth/change-password", {
        currentPassword,
        newPassword,
        signOutOthers
      });

      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      toast.success(signOutOthers ? "Password changed! Other sessions signed out." : "Password changed successfully!");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  const isPasswordValid = newPassword && newPassword.length >= 8 && newPassword === confirmPassword;

  return (
    <div style={{ maxWidth: 960, margin: "0 auto", padding: "10px 0 40px" }}>
      {/* ── 1. Profile Card ── */}
      <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 16, padding: 24, display: "flex", gap: 24, alignItems: "center", boxShadow: "var(--shadow)" }}>
        <div style={{ position: "relative", cursor: "pointer" }} onClick={() => fileInputRef.current?.click()}>
          {form.avatar ? (
            <img src={form.avatar} alt="DP" style={{ width: 80, height: 80, borderRadius: "50%", objectFit: "cover", border: "2px solid var(--border)" }} />
          ) : (
            <div style={{ width: 80, height: 80, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--primary)", color: "#fff", fontWeight: 800, fontSize: 24 }}>
              {uploadingAvatar ? "..." : initialsOf(form.name || form.email)}
            </div>
          )}
          <div style={{ position: "absolute", bottom: -5, right: -5, background: "var(--surface)", borderRadius: "50%", padding: 4, border: "1px solid var(--border)", display: "flex", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
            <Camera size={16} color="var(--text)" />
          </div>
          <input type="file" ref={fileInputRef} onChange={handleAvatarChange} accept="image/*" style={{ display: "none" }} />
        </div>

        <div style={{ flex: 1, overflow: "hidden" }}>
          <div style={{ fontWeight: 800, fontSize: 22, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "var(--text)" }}>{form.name || "Unnamed User"}</div>
          <div style={{ display: "flex", gap: 12, marginTop: 8, flexWrap: "wrap" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 14, color: "var(--muted)" }}><Mail size={16} /> {form.email || "No email"}</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 14, color: "var(--muted)" }}><Shield size={16} /> {form.role}</span>
          </div>
        </div>
        <button className="btn btn-primary" onClick={onSave} disabled={saving} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 20px", borderRadius: 12 }}>
          <Save size={16} /> {saving ? "Saving..." : "Save Profile"}
        </button>
      </div>

      {/* ── 2. Profile Details Form ── */}
      <div style={{ marginTop: 24, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 16, padding: 24, display: "grid", gap: 16, boxShadow: "var(--shadow)" }}>
        <h4 style={{ fontSize: "16px", fontWeight: 700, margin: 0, color: "var(--text)" }}>Personal Details</h4>
        <label style={{ display: "grid", gap: 8 }}>
          <span style={{ fontSize: 14, color: "var(--text)", fontWeight: 600 }}>Full Name</span>
          <input className="form-control" value={form.name} onChange={onChange("name")} placeholder="Enter your name" style={{ padding: 12, borderRadius: 12, background: "var(--bg)", border: "1px solid var(--border)", color: "var(--text)" }} />
        </label>
        <label style={{ display: "grid", gap: 8 }}>
          <span style={{ fontSize: 14, color: "var(--text)", fontWeight: 600 }}>Email</span>
          <input className="form-control" value={form.email} disabled placeholder="Email" style={{ padding: 12, borderRadius: 12, background: "rgba(0,0,0,0.1)", border: "1px solid var(--border)", color: "var(--muted)" }} />
        </label>
        <div style={{ display: "grid", gap: 16, gridTemplateColumns: "1fr 1fr" }}>
          <label style={{ display: "grid", gap: 8 }}>
            <span style={{ fontSize: 14, color: "var(--text)", fontWeight: 600 }}><Phone size={16} style={{ verticalAlign: "-3px" }} /> Phone</span>
            <input className="form-control" value={form.phone} onChange={onChange("phone")} placeholder="e.g. +92..." style={{ padding: 12, borderRadius: 12, background: "var(--bg)", border: "1px solid var(--border)", color: "var(--text)" }} />
          </label>
          <label style={{ display: "grid", gap: 8 }}>
            <span style={{ fontSize: 14, color: "var(--text)", fontWeight: 600 }}><MapPin size={16} style={{ verticalAlign: "-3px" }} /> Address</span>
            <input className="form-control" value={form.address} onChange={onChange("address")} placeholder="City, Country" style={{ padding: 12, borderRadius: 12, background: "var(--bg)", border: "1px solid var(--border)", color: "var(--text)" }} />
          </label>
        </div>
      </div>

      {/* ── 3. Change Password Card ── */}
      <div style={{ marginTop: 24, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 16, padding: 24, boxShadow: "var(--shadow)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(10,132,255,0.12)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--primary)" }}>
            <Key size={18} />
          </div>
          <div>
            <h4 style={{ fontSize: "16px", fontWeight: 700, margin: 0, color: "var(--text)" }}>Change Password</h4>
            <p style={{ fontSize: "13px", color: "var(--muted)", margin: 0 }}>Ensure your account stays secure by using a strong password.</p>
          </div>
        </div>

        <form onSubmit={handleChangePassword}>
          <div style={{ display: "grid", gap: 14, maxWidth: 540 }}>
            <PasswordInput
              label="Current Password"
              required
              name="currentPassword"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Enter current password"
              autoComplete="current-password"
            />

            <PasswordInput
              label="New Password"
              required
              name="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password (min 8 characters)"
              autoComplete="new-password"
            />

            {newPassword && (
              <PasswordStrengthMeter password={newPassword} confirmPassword={confirmPassword} />
            )}

            <PasswordInput
              label="Confirm New Password"
              required
              name="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your new password"
              autoComplete="new-password"
              error={confirmPassword && newPassword !== confirmPassword ? "Passwords do not match" : null}
            />

            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "13px", color: "var(--text)", cursor: "pointer", marginTop: 4 }}>
              <input
                type="checkbox"
                checked={signOutOthers}
                onChange={(e) => setSignOutOthers(e.target.checked)}
                style={{ width: 16, height: 16, accentColor: "var(--primary)" }}
              />
              <span>Sign out of all other devices on this password update</span>
            </label>

            <div>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={changingPassword || !isPasswordValid || !currentPassword}
                style={{ padding: "10px 24px", borderRadius: 10, fontWeight: 600 }}
              >
                {changingPassword ? "Updating Password..." : "Update Password"}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* ── 4. Active Sessions & Device Management ── */}
      <ActiveSessions />
    </div>
  );
}
